import { Button, Loader, RadioGroup, Icon } from '@pwc/pwc-deals-ui';
import { Constants } from '@pwc/pwc-deals-web-common';
import React, { useEffect, useState, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import usePowerBiUrl from '../../../common/hooks/usePowerBiUrl';
import { postPowerBiReport, postPowerBiPage } from '../libraryItemService';
import { getReports } from '../../analyses/powerBiService';
import './PowerBiIngestionModalBody.scss';
import PowerBiSelectionContainer from './PowerBiSelectionContainer';

export const PowerBiIngestionModalBody = ({
  pbiItems,
  handleSuccessfulIngestion,
  projectId,
}) => {
  const [reportList, setReportList] = useState([]);
  const [filteredReportList, setFilteredReportList] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState('');
  const [selectedPage, setSelectedPage] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [ingestionType, setIngestionType] = useState('');
  const [ingestionError, setIngestionError] = useState('');
  const powerBiUrl = usePowerBiUrl();

  const refreshReports = useCallback(() => {
    setIsLoading(true);
    getReports(projectId).then((reports) => {
      const formattedReports = reports
        .map((report) => ({
          label: report.name,
          value: report.reportId,
        }))
        .sort((a, b) => (a.label > b.label ? 1 : -1));

      setReportList(formattedReports);

      const pbiReportIds = pbiItems.map((p) => JSON.parse(p.data).ReportId);
      const filteredReports = formattedReports.filter(
        (r) => pbiReportIds.indexOf(r.value) === -1
      );
      setFilteredReportList(filteredReports);
      setIsLoading(false);
    });
  }, [pbiItems, projectId]);

  useEffect(() => {
    refreshReports([pbiItems, projectId]);
  }, [pbiItems, projectId, refreshReports]);

  const handleIngest = async () => {
    const reportName = reportList.filter((o) => o.value === selectedReportId)[0]
      .label;
    if (ingestionType === Constants.POWERBI_INGESTION_TYPES.report) {
      try {
        await postPowerBiReport(projectId, selectedReportId, reportName);
        handleSuccessfulIngestion();
      } catch (error) {
        setIngestionError('Error ingesting Power Bi Report');
      }
    } else {
      const { name, displayName } = selectedPage;
      const pageDisplayNameWithReportName = `${reportName} - ${displayName}`;
      try {
        await postPowerBiPage(
          projectId,
          selectedReportId,
          name,
          pageDisplayNameWithReportName
        );
        handleSuccessfulIngestion();
      } catch (error) {
        setIngestionError('Error ingesting Power Bi Report Tab');
      }
    }
  };

  const handleIngestionTypeChange = (value) => {
    if (value === Constants.POWERBI_INGESTION_TYPES.report) {
      setSelectedReportId('');
    }
    setIngestionType(value);
  };

  const ingestionTypeField = {
    name: 'ingestion-type',
    onChange: (event) => handleIngestionTypeChange(event.target.value),
    value: ingestionType,
  };

  const ingestionTypeOptions = [
    {
      key: Constants.POWERBI_INGESTION_TYPES.report,
      label: 'Report',
      value: Constants.POWERBI_INGESTION_TYPES.report,
    },
    {
      key: Constants.POWERBI_INGESTION_TYPES.page,
      label: 'Tab',
      value: Constants.POWERBI_INGESTION_TYPES.page,
    },
  ];

  return (
    <>
      <Modal.Body>
        {isLoading && <Loader dataTestId="power-bi-reports-loader" />}
        {!isLoading &&
          (filteredReportList.length > 0 ? (
            <>
              <div className="radio-group-label">Select type</div>
              <div className="import-hint">
                <Icon
                  name="information"
                  className="fill icon-yellow"
                  width={16}
                  data-testid="hint-icon"
                />
                <p>
                  The Power BI report must be published to Workbench to appear
                  as an option here.
                </p>
              </div>
              <RadioGroup
                field={ingestionTypeField}
                options={ingestionTypeOptions}
                direction="column"
                className="layout-radio-group"
              />

              <PowerBiSelectionContainer
                ingestionType={ingestionType}
                reportOptions={reportList}
                filteredReportOptions={filteredReportList}
                setSelectedReportId={setSelectedReportId}
                selectedReportId={selectedReportId}
                selectedPage={selectedPage}
                setSelectedPage={setSelectedPage}
                projectId={projectId}
              />
            </>
          ) : (
            <>
              <div className="no-results-header">
                <Icon name="alert" width={24} className="fill icon-yellow" />
                <p className="no-results-title">No Results Found</p>
              </div>
              <div className="no-results-body">
                <p>
                  All Power BI reports in the workspace have been imported to
                  Junction.
                </p>
                <p data-testid="workbench-link-copy">
                  Please make sure your report is published to{' '}
                  <a data-testid="workbench-link" href={powerBiUrl}>
                    Workbench
                  </a>
                  , or check the library if the data has already been imported.
                </p>
                <div>
                  <button
                    className="ui-button button-link refresh-button"
                    data-testid="refresh-button"
                    onClick={() => refreshReports()}
                    type="button"
                  >
                    <Icon
                      name="refresh"
                      width={24}
                      className="fill icon-orange-theme"
                    />
                    Refresh data
                  </button>
                </div>
              </div>
            </>
          ))}
      </Modal.Body>
      <Modal.Footer>
        {filteredReportList.length > 0 && (
          <div className="modal-actions">
            <span>{ingestionError}</span>
            <Button
              className="button"
              onClick={handleIngest}
              type="button"
              data-testid="ingest-powerbi-submit"
              disabled={
                !selectedReportId ||
                (ingestionType === Constants.POWERBI_INGESTION_TYPES.page &&
                  !selectedPage.name)
              }
            >
              Import
            </Button>
          </div>
        )}
      </Modal.Footer>
    </>
  );
};
